import React, { ReactNode } from "react"
import {
  Text, VStack, FormControl, Box, Radio, RadioGroup, Stack,
} from "@chakra-ui/react"
import { Control, useController, useWatch } from "react-hook-form"
import EditorPane from "components/CanvasEditor/EditorPane"
import { BadgeQRFormat, BADGE_QR_FORMAT_LABELS } from "sharedTypes/badge"
import { QR_MIN_SIZES_MM } from "components/BadgeBuilder/utils/badgeHelpers"
import { BadgeElementFormValues } from "../BadgeSidePanelForm"

type Props = {
  control: Control<BadgeElementFormValues>
}

const BadgeQRCodeFields = ({ control }: Props) => {
  const { field } = useController({ name: "format", control })
  const width = useWatch({ control, name: "width" })
  const height = useWatch({ control, name: "height" })

  const handleFormatChange = (newValue: string) => {
    const newFormat = newValue as BadgeQRFormat
    if (newFormat === BadgeQRFormat.VCARD) {
      const minSize = QR_MIN_SIZES_MM[BadgeQRFormat.VCARD]
      if (typeof width === "number" && typeof height === "number" && (width < minSize || height < minSize)) {
        // eslint-disable-next-line no-alert
        const confirmed = window.confirm(
          `The minimum size for a vCard QR code is ${minSize}\u00D7${minSize}mm. `
          + `Your QR code (${width}\u00D7${height}mm) will be resized to meet this requirement.`,
        )
        if (!confirmed) return
      }
    }
    field.onChange(newFormat)
  }

  const descriptions: Record<BadgeQRFormat, ReactNode> = {
    [BadgeQRFormat.GUEST_ID]: (
      <AdditionalElementWrapper>
        <VStack spacing={1} align="stretch">
          <Text fontSize="sm" color="gray.800" lineHeight="1.4">
            Simple check-in code for guest identification and event access.
          </Text>
          <Text fontSize="xs" color="blue.600" fontWeight="medium" lineHeight="1.4">
            Minimum size: {QR_MIN_SIZES_MM[BadgeQRFormat.GUEST_ID]}mm
            {" "}&times; {QR_MIN_SIZES_MM[BadgeQRFormat.GUEST_ID]}mm
          </Text>
        </VStack>
      </AdditionalElementWrapper>
    ),
    [BadgeQRFormat.VCARD]: (
      <AdditionalElementWrapper>
        <VStack spacing={2} align="stretch" color="gray.800">
          <Text fontSize="sm" lineHeight="1.4">
            This QR Code can be used by both the Diobox QR Scanner to check in guests and
            anyone else to facilitate the exchange of virtual business cards.
          </Text>
          <Text fontSize="sm" lineHeight="1.4">
            Any smartphone camera can scan these QR codes and add the guest contact info
            to its address book. No app download is necessary — the QR code translates
            into a standard vCard format.
          </Text>
          <Text fontSize="sm" fontWeight="semibold" lineHeight="1.4">
            The following guest fields will be included in the vCard:
          </Text>
          <VStack spacing={0} align="stretch" pl={2}>
            <Text fontSize="sm" lineHeight="1.4">• Name</Text>
            <Text fontSize="sm" lineHeight="1.4">• Title</Text>
            <Text fontSize="sm" lineHeight="1.4">• Company</Text>
            <Text fontSize="sm" lineHeight="1.4">• Email Address</Text>
            <Text fontSize="sm" lineHeight="1.4">• Phone Number</Text>
          </VStack>
          <Text fontSize="sm" fontStyle="italic" lineHeight="1.4">
            Note: QR Codes that encapsulate the guest vCard data will be visually
            more condensed and may affect the overall speed and success of the scanning.
            Devices with older cameras may need to get closer to have a successful scan.
          </Text>
          <Text fontSize="xs" color="blue.600" fontWeight="medium" lineHeight="1.4" mt={1}>
            Minimum size: {QR_MIN_SIZES_MM[BadgeQRFormat.VCARD]}mm
            {" "}&times; {QR_MIN_SIZES_MM[BadgeQRFormat.VCARD]}mm
          </Text>
        </VStack>
      </AdditionalElementWrapper>
    ),
  }

  return (
    <EditorPane title="QR Code Type">
      <FormControl mb={4}>
        <RadioGroup
          w="100%"
          value={field.value}
          onChange={handleFormatChange}
        >
          <Stack direction="column" spacing={3}>
            {Object.values(BadgeQRFormat).map((format) => (
              <React.Fragment key={format}>
                <Radio
                  value={format}
                  w="100%"
                  alignItems="flex-start"
                >
                  <Box ml={1} mt="-6px">{BADGE_QR_FORMAT_LABELS[format]}</Box>
                </Radio>
                {descriptions[format]}
              </React.Fragment>
            ))}
          </Stack>
        </RadioGroup>
      </FormControl>
    </EditorPane>
  )
}

const AdditionalElementWrapper = ({ children }: {children: ReactNode}) => (<Box w="100%" pl={7} pr={6} mt={-2} mb={2} whiteSpace="normal" overflowWrap="break-word">{children}</Box>)

export default BadgeQRCodeFields
